import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useCart } from '../../hooks/useCart';
import { useCookies } from '../../hooks/useCookies';
import { useUser } from '../../hooks/useUser';
import { usePrevious } from '../../hooks/usePrevious';
import { useLastValue } from '../../hooks/useLastValue';

const JUST_ADDED_DURATION_MS = 4000;

export const useJustAddedItem = () => {
  const [justAddedItemId, setJustAddedItemId] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    user: { isLoggedIn }
  } = useUser();
  const {
    cookies: { cartId }
  } = useCookies();
  const { cart, loaded } = useCart();
  const cartItems = useMemo(() => cart.attributes.cartItems.map(item => item.data), [cart]);
  const lastCartId = useLastValue(cartId);
  const previousCartItems = usePrevious(cartItems);
  const previousLoaded = usePrevious(loaded);

  const newCartItem = useMemo(
    () =>
      cartItems.find(item => !previousCartItems?.find(previousItem => previousItem.id === item.id)),
    [cartItems, previousCartItems]
  );

  const isNewCart = !isLoggedIn && lastCartId === null;

  useEffect(() => {
    if (newCartItem && (previousLoaded || isNewCart)) {
      setJustAddedItemId(newCartItem.id);
    }
  }, [newCartItem, setJustAddedItemId, previousLoaded, isNewCart]);

  useEffect(() => {
    if (justAddedItemId) {
      timeoutRef.current = global.setTimeout(
        () => setJustAddedItemId(null),
        JUST_ADDED_DURATION_MS
      );
      return () => {
        timeoutRef.current && clearTimeout(timeoutRef.current);
      };
    }
  }, [justAddedItemId, setJustAddedItemId, timeoutRef]);

  const justAddedItem = useMemo(() => cartItems.find(item => item.id === justAddedItemId) || null, [
    cartItems,
    justAddedItemId
  ]);

  const dismiss = useCallback(() => setJustAddedItemId(null), [setJustAddedItemId]);
  const persist = useCallback(() => timeoutRef.current && clearTimeout(timeoutRef.current), [
    timeoutRef
  ]);

  return {
    justAddedItem,
    dismiss,
    persist
  };
};
